<script lang="ts">
  /**
   * Change password: two fields and one action, in the account form's dress
   * (the shell's `FullPage`, back dress).
   *
   * Reached from the profile sheet. It exists because an account here has no
   * email (`docs/DESIGN.md`): the only way a password changes is the person
   * changing it, and until `docs/decisions/0052` there was no way, so a
   * temporary password an administrator issued stayed the password, and one
   * that leaked could not be replaced.
   *
   * The server asks for the current password as well as the session, holds
   * the new one to the registration rules, and signs every *other* device
   * out. This one stays signed in, which is why nothing here reloads the
   * account.
   *
   * The page's content exists only while it is open (the shell's `FullPage`
   * renders it only then), so no page holds a second pair of password inputs
   * for a password manager to find, and the fields are new and empty every
   * time.
   */
  import { Field, FullPage, Note, PillButton } from '@artibens/app-shell';

  import { changePassword } from '../state/account.svelte.js';

  const { open, onclose }: { open: boolean; onclose: () => void } = $props();

  /** The registration rule, so the button is not offered for a password the server will refuse. */
  const MIN_LENGTH = 8;

  let current = $state('');
  let next = $state('');
  let busy = $state(false);
  let error = $state('');
  let changed = $state(false);

  const canSubmit = $derived(current.length > 0 && next.length >= MIN_LENGTH);

  let wasOpen = false;
  $effect(() => {
    if (open && !wasOpen) {
      current = '';
      next = '';
      error = '';
      changed = false;
    }
    wasOpen = open;
  });

  async function submit(): Promise<void> {
    if (!canSubmit || busy) return;
    busy = true;
    error = '';
    changed = false;
    try {
      const refusal = await changePassword(current, next);
      if (refusal === null) {
        changed = true;
        current = '';
        next = '';
      } else {
        error = refusal;
      }
    } finally {
      busy = false;
    }
  }
</script>

<FullPage {open} title="Change password" dress="back" {onclose} testid="change-password-sheet">
  <form
    class="password-form"
    onsubmit={(event) => {
      event.preventDefault();
      void submit();
    }}
  >
    <Field
      label="Current password"
      type="password"
      autocomplete="current-password"
      bind:value={current}
      testid="password-current"
    />
    <Field
      label="New password"
      type="password"
      placeholder="At least 8 characters"
      autocomplete="new-password"
      bind:value={next}
      testid="password-new"
    />

    {#if error}
      <Note tone="error" testid="password-error">{error}</Note>
    {/if}
    {#if changed}
      <Note tone="ink" status testid="password-changed">
        Password changed. Your other devices have been signed out.
      </Note>
    {/if}

    <PillButton
      type="submit"
      label="Change password"
      size="lg"
      disabled={!canSubmit}
      {busy}
      testid="password-submit"
    />
  </form>
</FullPage>

<style>
  .password-form {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-4);
    padding: var(--ui-space-2) 0 0;
  }
</style>
